"use client";
import {Download, GetApp, KeyboardArrowDown, KeyboardArrowUp, RadioButtonUnchecked, UploadFile} from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FlagIcon from "@mui/icons-material/Flag";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import {Box, Button, CircularProgress, FormControl, Grid, IconButton, InputLabel, Menu, MenuItem, Pagination, Paper, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography} from "@mui/material";
import Chip from "@mui/material/Chip";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {formatDate} from "app/utils/date";
import ExcelJS from "exceljs";
import moment from "moment";
import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {toast} from "react-toastify";
import {axios_tenant} from "../../../axios";
import SLAUpdateModal from "./updateSLA";
import UploadReferencesDocsDialog from "./uploadReferenceDocsDialog.js";
import {INVALID_REASONS, getInvalidReasonLabel, resolveInvalidReasonCode} from "./utils/invalidReasons";

const StatusIcon = ({status, remarks, source}) => {
    if (status === "Available") {
        if (source === "User") {
            return <HowToRegOutlinedIcon sx={{color: "#0D93D2"}}/>;
        }
        return <CheckCircleOutlineIcon color="success"/>;
    } else if (status === "Pending") {
        // Show remarks on hover for Pending status
        return (
            <Tooltip title={remarks || ""} arrow>
                <span>
                    <RadioButtonUncheckedIcon/>
                </span>
            </Tooltip>
        );
    } else if (status === "N/A") {
        return <Chip label="Not applicable" sx={{border: source === "User" ? "2px solid #0D93D2" : undefined}}/>;
    } else if (status === "NotAvailable") {
        return <Chip label={remarks} sx={{border: source === "User" ? "2px solid #0D93D2" : undefined}}/>;
    } else if (status === "Downloading") {
        if (source === "User") {
            return <HowToRegOutlinedIcon sx={{color: "#D5C21E"}}/>;
        }
        return <CheckCircleOutlineIcon color="warning"/>;
    }
    return (
        <RadioButtonUnchecked fontSize="small" sx={{color: "text.disabled"}}/>
    );
};

StatusIcon.propTypes = {
    status: PropTypes.string,
    remarks: PropTypes.string,
    source: PropTypes.string,
};

function generateUserNameReason(source, remarks, status) {
    let userAndReason = "";
    if(status == "Pending"){
        return "";
    }
    userAndReason = source?.source === "User" ? source.userName : "System";
    switch (status) {
        case "Available":
            return userAndReason;
        case  "N/A":
            return `${userAndReason} - Not applicable`;
        case "NotAvailable":
            return `${userAndReason} - ${remarks}`;
        default:
            return userAndReason;
    }
}

function generateMetadataUserName(metadataSource) {
    if (metadataSource?.source === "User") {
        return metadataSource.userName;
    }
    return metadataSource?.source ? "System" : "";
}

const getStatusColor = status => {
    switch (status) {
        case "Under Review":
            return "#EA8407";
        case "In Progress":
            return "#FCD426";
        case "To Do":
            return "#0D93D2";
        case "Completed":
        case "Auto Completed":
            return "#24BC7C";
        default:
            return "inherit";
    }
};

export default function ReferenceTable({requests, loadRequests, loading, setLoading}) {
    const [tab, setTab] = React.useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [receivedOn, setReceivedOn] = useState("");
    const [status, setStatus] = useState("");

    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [expandedRequest, setExpandedRequest] = React.useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPatent, setSelectedPatent] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [pendingRequests, setPendingRequests] = useState(requests.filter(request => !request.status.includes("Completed") && request.nonEnglishPatents === true));
    const [completedRequests, setCompletedRequests] = useState(
        requests
            .filter(request => request.status.includes("Completed"))
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
    );
    const [slaModalOpen, setSLAModalOpen] = useState(false);
    // One flag menu serves every row: it remembers which reference opened it so the chosen
    // reason is applied to that RequestPatent.
    const [flagMenuAnchor, setFlagMenuAnchor] = useState(null);
    const [flaggedPatent, setFlaggedPatent] = useState(null);
    const searchHandler = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
    };

    const formatReference = (patent) => {
        return patent.country + patent.patentNumber + patent.kindCode;
    };

    useEffect(() => {
        setPage(1);
        setPendingRequests(requests.filter(request => !request.status.includes("Completed") && request.nonEnglishPatents === true));
        setCompletedRequests(
            requests
                .filter(request => request.status.includes("Completed"))
                .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
        );

    }, [tab]);

    useEffect(() => {
        const pending = requests.filter(request => !request.status.includes("Completed") && request.nonEnglishPatents === true);
        const completed = requests
            .filter(request => request.status.includes("Completed"))
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

        setPendingRequests(pending);
        setCompletedRequests(completed);

        if (
            (tab === 0 && page > Math.ceil(pending.length / rowsPerPage)) ||
            (tab === 1 && page > Math.ceil(completed.length / rowsPerPage))
        ) {
            setPage(1);
        }
    }, [requests]);

    // Filtered Requests
    const filteredRequests = useMemo(() => {
            let selectedTabRequests = tab === 0 ? pendingRequests : completedRequests;
            // Filter by search term
            return selectedTabRequests
                // .filter((request) => {
                //   // Tab filter
                //   if (tab === 0 && request.status.includes("Completed")) return false;
                //   if (tab === 1 && !request.status.includes("Completed")) return false;
                //   return true;
                // })
                .filter((request) => {
                    if (!searchTerm) return true;
                    const searchTermLower = searchTerm.toLowerCase();
                    return (
                        request.requestId.toLowerCase().includes(searchTermLower) ||
                        request.tenant.toLowerCase().includes(searchTermLower) ||
                        request.RequestPatents.some((requestPatent) =>
                            formatReference(requestPatent.Patent).toLowerCase().includes(searchTermLower)
                        )
                    );
                })
                .filter((request) => {
                    if (!receivedOn) return true;
                    return moment(request.createdAt).format("YYYY-MM-DD") === receivedOn;
                })
                .filter((request) => {
                    if (!status) return true;
                    return request.status === status;
                });
        }, [pendingRequests, completedRequests, tab, searchTerm, receivedOn, status]
    );

    // Handle Pagination
    const paginatedRequests = useMemo(() => {
        if (filteredRequests.length === 0) return []; // Edge case

        return filteredRequests.slice(
            (page - 1) * rowsPerPage, // Map 1-based page to zero-based index
            page * rowsPerPage // Correct upper bound
        );
    }, [filteredRequests, page, rowsPerPage]);


    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const handleUploadClick = (patent, requestId) => {
        setSelectedPatent(patent);
        setSelectedRequestId(requestId);
        setModalOpen(true);
    };

    const handleCloseModal = async () => {
        setModalOpen(false);
        setSelectedPatent(null);
        setSelectedRequestId(null);
    };

    const handleSLAUpdate = (request) => {
        setSLAModalOpen(true);
        setSelectedRequest(request);
    };

    const handleCloseSlaModal = () => {
        setSLAModalOpen(false);
        setSelectedPatent(null);
        setSelectedRequest(null);
    };


    const toggleExpand = requestId => {
        setExpandedRequest(expandedRequest === requestId ? null : requestId);
    };

    const handleDownloadRequestsSpreadsheet = async () => {
        setLoading(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Requests");
            worksheet.addRow([
                "Tenant name",
                "Request ID",
                "Request Date & Time",
                "Status",
                "Reference Count",
                "SLA/Completion Time",
                "Abstract Source",
                "Original Source",
                "Metadata Source"
            ]);
            worksheet.columns = [
                {width: 25},
                {width: 25},
                {width: 25},
                {width: 25},
                {width: 25},
                {width: 25},
                {width: 30},
                {width: 30},
                {width: 30}
            ];
            let selectedTabRequests = tab === 0 ? pendingRequests : completedRequests;
            selectedTabRequests.forEach(request => {
                let dateTime = `${formatDate(request.createdAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.createdAt))}`;
                // let slaCompletionTime = request.slaTime ?  : "";
                let slaCompletionTime = tab === 0 ?
                    `${formatDate(request.slaTime)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.slaTime))}`
                    : `${formatDate(request.completedAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.completedAt))}`;
                const row = worksheet.addRow([
                    request.tenant,
                    request.requestId,
                    dateTime,
                    request.status,
                    request.RequestPatents.length,
                    slaCompletionTime
                ]);
                row.alignment = { wrapText: true, vertical: 'top' };
                row.font = {bold: true};
                request.RequestPatents.forEach(patent => {
                        const patentRow = worksheet.addRow([
                            "",
                            "",
                            "",
                            "",
                            patent.Patent.country + patent.Patent.patentNumber + patent.Patent.kindCode,
                            "",
                            generateUserNameReason(patent?.Patent?.abstractSource, patent.Patent.abstractRemarks, patent.Patent.abstractStatus),
                            generateUserNameReason(patent?.Patent?.documentSource, patent.Patent.documentRemarks, patent.Patent.documentStatus),
                            generateMetadataUserName(patent?.Patent?.metadataSource),
                        ]);
                        patentRow.alignment = { wrapText: true, vertical: 'top' };
                    }
                );
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const today = new Date();
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const formattedDate = `${today.getDate().toString().padStart(2, "0")}-${monthNames[today.getMonth()]}-${today.getFullYear()}`;
            link.setAttribute("download", `Reference_Request_Report_${formattedDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Error downloading requests spreadsheet");
        } finally {
            setLoading(false);
        }
    };

    const downloadZip = async (type, number) => {
        setLoading(true);
        try {
            // Make a GET request to fetch the zip file
            const url = type === "reference" ? `/reference-downloader/patent/${number}` : `/reference-downloader/zip/${number}`;
            const response = await axios_tenant.get(url, {
                responseType: "blob" // This is crucial to handle binary data (zip file)
            });

            // Check if the response contains the file data
            if (response.status === 200) {
                // Create a URL for the Blob
                const url = window.URL.createObjectURL(new Blob([response.data]));

                // Create a link element to trigger the download
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `${number}.zip`); // Use patentNumber to set the filename
                // Trigger the download by clicking the link
                document.body.appendChild(link);
                link.click();

                // Clean up the URL object after download
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else if (response.error) {
                toast.error(response.error);
            } else {
                toast.error(`Failed to download the ${type} files.`);
            }
        } catch (error) {
            console.error(`Failed to download the ${type} files.`, error);
            toast.error(`Failed to download the ${type} files.`);
        } finally {
            setLoading(false);
        }
    };

    const handleFlagClick = (event, patent) => {
        setFlagMenuAnchor(event.currentTarget);
        setFlaggedPatent(patent);
    };

    const handleFlagMenuClose = () => {
        setFlagMenuAnchor(null);
        setFlaggedPatent(null);
    };

    // invalidReason is null when marking the reference valid again; the API clears the
    // stored reason in that case.
    const handleInvalidChange = async (patentId, isInvalid, invalidReason) => {
        setLoading(true);
        let status = isInvalid ? getInvalidReasonLabel(invalidReason) : "Valid";
        try {
            await axios_tenant.put("/reference-downloader/requestpatent/invalid", {
                patentId,
                isInvalid,
                invalidReason
            });
            await loadRequests();
            toast.success(`Reference marked as ${status}`);
        } catch (error) {
            toast.error(`Failed to mark reference as ${status}`);
            console.error(`Failed to mark reference as ${status}`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleReasonSelect = (invalidReason) => {
        const patent = flaggedPatent;
        handleFlagMenuClose();
        if (!patent) return;
        handleInvalidChange(patent.requestPatentId, invalidReason !== null, invalidReason);
    };

    const submitForReview = async (requestId, loadRequests) => {
        setLoading(true);
        try {
            await axios_tenant.put("/reference-downloader/requests/submitForReview", {
                requestId: requestId
            });
            await loadRequests();
            toast.success("Request submitted for review");
        } catch (error) {
            toast.error("Failed to submit request for review");
            console.error("Failed to submit request for review", error);
        }
        setLoading(false);
    };

    const markRequestCompleted = async (requestId, loadRequests) => {
        setLoading(true);
        try {
            await axios_tenant.put("/reference-downloader/requests/complete", {
                requestId: requestId
            });
            await loadRequests();
            toast.success("Request marked as completed");
        } catch (error) {
            toast.error("Failed to mark request as completed");
            console.error("Failed to mark request as completed", error);
        }
        setLoading(false);
    };

    return (
        <>
            <Box sx={{width: "100%"}}>
                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.7)"
                        }}
                    >
                        <CircularProgress/>
                    </Box>
                )}
                <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2}}>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        TabIndicatorProps={{style: {background: "#06F"}}}
                    >
                        <Tab
                            label="Pending"
                            sx={{color: tab === 0 ? "#06F" : "inherit"}}
                        />
                        <Tab label="Completed"/>
                    </Tabs>
                </Box>

                <Stack direction="row" spacing={2} sx={{mt: 2, alignItems: "center", justifyContent: "space-between"}}>
                    {/* Left: Filters */}
                    <Stack direction="row" spacing={2} alignItems="center">

                        {/* Received On (Date Picker) */}
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Received On"
                                value={receivedOn ? moment(receivedOn, "YYYY-MM-DD") : null} // Ensure valid moment object
                                onChange={(newValue) => {
                                    setReceivedOn(newValue ? moment(newValue).format("YYYY-MM-DD") : "");
                                }}
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        size="small"
                                        InputLabelProps={{
                                            shrink: !!receivedOn
                                        }}
                                    />}
                                sx={{minWidth: 120, maxWidth: 200}}
                                disableFuture // Disable future dates
                            />
                        </LocalizationProvider>

                        {/* Status Selector */}
                        <FormControl size="small" sx={{minWidth: 240}}>
                            <InputLabel id="status-select-label">Select Status</InputLabel>
                            <Select
                                labelId="status-select-label"
                                id="status-select"
                                value={status}
                                label="Select Status"
                                onChange={(e) => setStatus(e.target.value)}
                                // onChange={handleStatusChange}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="To Do">To Do</MenuItem>
                                <MenuItem value="In Progress">In Progress</MenuItem>
                                <MenuItem value="Under Review">Under Review</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                                <MenuItem value="Auto Completed">Auto Completed</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            sx={{
                                borderRadius: "40px",
                                backgroundColor: "white",
                                border: "1px solid #E0E0E0",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none"
                                }
                            }}
                            size="small"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => searchHandler(e)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{mr: 1, color: "text.secondary"}}/>
                            }}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            component="span"
                            disabled={loading}
                            startIcon={<Download/>}
                            onClick={handleDownloadRequestsSpreadsheet}
                        >
                            Download Requests
                        </Button>
                    </Stack>
                </Stack>

                {/* Header row */}

                {(tab === 0 && pendingRequests.length === 0) || (tab === 1 && completedRequests.length === 0) ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                mt: 8
                            }}
                        >
                            <Typography
                                variant="h4"
                                color="text.secondary"
                                sx={{
                                    mb: 2,
                                    fontWeight: 600
                                }}
                            >
                                No requests found
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Please check back later or contact your administrator.
                            </Typography>
                        </Box>
                    )
                    : (
                        <Grid container
                              sx={{
                                  p: 2,
                                  // backgroundColor: "#f5f5f5",
                                  // mb: 2,
                                  borderRadius: "4px"
                              }}>
                            <Grid item xs={2}>
                                <Typography variant="subtitle2" color="text.secondary">Tenant Name</Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="subtitle2" color="text.secondary">No. of Ref</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="subtitle2" color="text.secondary">Request ID</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="subtitle2" color="text.secondary">Request Time</Typography>
                            </Grid>
                            {tab === 1 && (
                                <Grid item xs={2}>
                                    <Typography variant="subtitle2" color="text.secondary">Completion Time</Typography>
                                </Grid>
                            )}
                            <Grid item xs={2}>
                                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                            </Grid>
                        </Grid>
                    )}
                <Box sx={{
                    // px: 2,
                    maxHeight: 480,
                    overflow: "auto",
                    "&::-webkit-scrollbar": {
                        width: "0.4em"
                    },
                    "&::-webkit-scrollbar-track": {
                        "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)"
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,0.1)",
                        borderRadius: "10px"
                    }
                }}>
                    {paginatedRequests.map(request => (
                        <Box key={request.requestId} sx={{mb: 2}}>
                            <Paper
                                key={request.requestId}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                    border: expandedRequest === request.requestId ? "1px solid #0066FF" : "1px solid #E0E0E0",
                                    borderRadius: "10px",
                                    // borderRadius: "8px",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                                }}
                            >
                                <Grid container
                                      alignItems="center"
                                      sx={{
                                          p: 2,
                                          backgroundColor: "#FFF"
                                      }}>

                                    {/* <TableCell sx={{ py: 1.5, width: "10%" }}> */}
                                    <Grid item xs={2}>
                                        <Box sx={{display: "flex", alignItems: "center"}}>
                                            <IconButton size="small" sx={{mr: 1}} onClick={() => toggleExpand(request.requestId)}>
                                                {expandedRequest === request.requestId ? (
                                                    <KeyboardArrowUp fontSize="small"/>
                                                ) : (
                                                    <KeyboardArrowDown fontSize="small"/>
                                                )}
                                            </IconButton>
                                            <Typography variant="subtitle1" sx={{fontWeight: "medium"}}>
                                                {request.tenant.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Typography>{request.RequestPatents.length}</Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="subtitle1" sx={{fontWeight: "medium"}}>
                                            {request.requestId}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={2}>
                                        <Typography variant="body2">
                                            {`${formatDate(request.createdAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.createdAt))}`}
                                        </Typography>
                                        {/* </TableCell> */}
                                    </Grid>

                                    {tab === 1 && (
                                        <Grid item xs={2}>
                                            <Typography variant="body2">
                                                {`${formatDate(request.completedAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.completedAt))}`}
                                            </Typography>
                                            {/* </TableCell> */}
                                        </Grid>
                                    )}

                                    <Grid item xs={2}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: getStatusColor(request.status),
                                                fontWeight: "medium"
                                            }}
                                        >
                                            {request.status}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={1}>
                                        <IconButton
                                            size="small"
                                            sx={{color: "primary.main"}}
                                            disabled={loading}
                                            onClick={() =>
                                                downloadZip("request", request.requestId)
                                            }
                                        >
                                            <GetApp
                                            />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{color: "primary.main"}}
                                            hidden={request.status.includes("Completed")}
                                            onClick={() => handleSLAUpdate(request)}
                                        >
                                            <ManageHistoryIcon/>
                                        </IconButton>
                                    </Grid>
                                </Grid>

                                {expandedRequest === request.requestId && (
                                    <Box>
                                        <TableContainer
                                            sx={{
                                                bgcolor: "white",
                                                // px: 2,
                                                maxHeight: 220,
                                                overflow: "auto",
                                                "&::-webkit-scrollbar": {
                                                    width: "0.4em"
                                                },
                                                "&::-webkit-scrollbar-track": {
                                                    "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)"
                                                },
                                                "&::-webkit-scrollbar-thumb": {
                                                    backgroundColor: "rgba(0,0,0,0.1)",
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        >
                                            <Table size="small" sx={{}}>
                                                <TableHead>
                                                    <TableRow sx={{"& th": {borderBottom: "none", borderTop: "1px solid #E0E0E0", color: "text.secondary", paddingTop: 2}}}>
                                                        <TableCell sx={{width: "20%", textAlign: "left"}}>Reference</TableCell>
                                                        <TableCell sx={{width: "20%", textAlign: "center"}}>Abstract</TableCell>
                                                        <TableCell sx={{width: "20%", textAlign: "center"}}>Original File</TableCell>
                                                        <TableCell sx={{width: "20%", textAlign: "center"}}>Metadata</TableCell>
                                                        <TableCell sx={{width: "20%", textAlign: "center"}}>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {request.RequestPatents.map((patent, index) => (
                                                        // <TableRow key={patent.requestPatentId} sx={{ "& td": { borderBottom: "none" } }}>
                                                        <TableRow
                                                            key={patent.requestPatentId}
                                                            sx={{"& td": {borderBottom: "none"}}}
                                                        >
                                                            <TableCell sx={{py: 1.5, width: "20%", textAlign: "left"}}>
                                                                <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                                                                    {/* A flagged reference is greyed to the same text.secondary as the table
                                                                        header (regular weight, not the header's medium) so it reads as struck
                                                                        off. No row-wide opacity: stacked on a lighter grey it was too faint (TAI-1129 UAT). */}
                                                                    <Typography variant="body2" component="span" sx={{color: patent.isInvalid ? "text.secondary" : "inherit"}}>
                                                                        {patent.Patent.country + patent.Patent.patentNumber + patent.Patent.kindCode}
                                                                    </Typography>
                                                                    {patent.isInvalid && (
                                                                        <Chip
                                                                            size="small"
                                                                            label={getInvalidReasonLabel(patent.invalidReason)}
                                                                            sx={{
                                                                                color: "error.main",
                                                                                backgroundColor: "#FDECEA",
                                                                                height: 20,
                                                                                fontSize: "0.7rem"
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{py: 1.5, width: "20%", textAlign: "center"}}>
                                                                <StatusIcon
                                                                    status={patent.Patent.abstractStatus}
                                                                    remarks={patent.Patent.abstractRemarks}
                                                                    source={patent.Patent.abstractSource?.source}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{py: 1.5, width: "20%", textAlign: "center"}}>
                                                                <StatusIcon
                                                                    status={patent.Patent.documentStatus}
                                                                    remarks={patent.Patent.documentRemarks}
                                                                    source={patent.Patent.documentSource?.source}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{py: 1.5, width: "20%", textAlign: "center"}}>
                                                                <StatusIcon
                                                                    status={patent.Patent.metadataStatus}
                                                                    remarks={patent.Patent.metadataRemarks}
                                                                    source={patent.Patent.metadataSource?.source}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{py: 1.5, width: "20%", textAlign: "center"}}>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{color: "primary.main"}}
                                                                    disabled={loading || request.status.includes("Completed") || patent.isInvalid}
                                                                >
                                                                    <UploadFile
                                                                        onClick={() => handleUploadClick(patent, request.requestId)}
                                                                    />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{color: "primary.main"}}
                                                                    disabled={loading || patent.isInvalid}
                                                                >
                                                                    <GetApp
                                                                        onClick={() =>
                                                                            downloadZip("reference", patent.Patent.country + patent.Patent.patentNumber + patent.Patent.kindCode)
                                                                        }
                                                                    />
                                                                </IconButton>
                                                                <Tooltip
                                                                    title={patent.isInvalid ? getInvalidReasonLabel(patent.invalidReason) : "Flag Reference"}
                                                                    placement="top"
                                                                >
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{color: patent.isInvalid ? "error.main" : "primary.main"}}
                                                                            disabled={loading || request.status.includes("Completed")}
                                                                            onClick={(event) => handleFlagClick(event, patent)}
                                                                        >
                                                                            {patent.isInvalid ? <FlagIcon/> : <OutlinedFlagIcon/>}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {request.RequestPatents.every((patent) =>
                                            (patent.Patent.abstractRemarks == "Invalid Reference" && patent.Patent.documentRemarks == "Invalid Reference")
                                            || patent.isInvalid ||
                                            (
                                                (patent.Patent.abstractStatus === "Available" || patent.Patent.abstractStatus === "N/A" || (patent.Patent.abstractStatus === "NotAvailable" && patent.Patent.abstractRemarks != null)) &&
                                                (patent.Patent.metadataStatus === "Available" || patent.Patent.metadataStatus === "N/A" || (patent.Patent.metadataStatus === "NotAvailable" && patent.Patent.metadataRemarks != null)) &&
                                                (patent.Patent.documentStatus === "Available" || patent.Patent.documentStatus === "N/A" || (patent.Patent.documentStatus === "NotAvailable" && patent.Patent.documentRemarks != null))
                                            )
                                        ) && request.status !== "Completed" && request.status !== "Auto Completed" && (
                                            <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%"
                                                 sx={{
                                                     bgcolor: "white",
                                                     px: 3
                                                 }}
                                            >
                                                {
                                                    request.status === "Under Review" ?
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={loading}
                                                            sx={{
                                                                my: 1
                                                            }}
                                                            onClick={() => markRequestCompleted(request.requestId, loadRequests)}
                                                        >
                                                            Submit
                                                        </Button>
                                                        :
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            disabled={loading}
                                                            sx={{my: 1}}
                                                            onClick={() => submitForReview(request.requestId, loadRequests)}
                                                        >
                                                            Save
                                                        </Button>
                                                }
                                            </Box>
                                        )
                                        }
                                    </Box>
                                )}
                                {/* </> */}
                            </Paper>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 2,
          mb: 2,
          px: 2
        }}
      > */}
            {/* Pagination & Rows Per Page Selector */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{mt: 2}}>
                <Grid item xs={4}/>

                {/* Pagination (Centered) */}
                <Grid item xs={4} container justifyContent="center">
                    <Pagination
                        count={Math.ceil(filteredRequests.length / rowsPerPage)}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                        siblingCount={1}
                        boundaryCount={1}
                    />
                </Grid>

                {/* Rows Per Page Selector (Right) */}
                <Grid item xs={4} container justifyContent="flex-end">
                    <FormControl size="small">
                        {/* <InputLabel>Rows Per Page</InputLabel> */}
                        <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(1);
                            }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body2" sx={{ml: 1, alignSelf: "center"}}>Rows Per Page</Typography>
                </Grid>
            </Grid>
            {/* </Box> */}
            {/* Reasons come from the shared catalog, so a new reason needs no change here. */}
            <Menu
                anchorEl={flagMenuAnchor}
                open={Boolean(flagMenuAnchor)}
                onClose={handleFlagMenuClose}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
            >
                {INVALID_REASONS.map((reason) => (
                    <MenuItem
                        key={reason.code}
                        selected={Boolean(flaggedPatent?.isInvalid) && resolveInvalidReasonCode(flaggedPatent.invalidReason) === reason.code}
                        onClick={() => handleReasonSelect(reason.code)}
                    >
                        {`Mark as ${reason.label}`}
                    </MenuItem>
                ))}
                {/* A reference stays revertible until the request is finally submitted. */}
                {flaggedPatent?.isInvalid && (
                    <MenuItem onClick={() => handleReasonSelect(null)}>
                        Mark as Valid
                    </MenuItem>
                )}
            </Menu>

            <UploadReferencesDocsDialog
                open={modalOpen}
                onClose={handleCloseModal}
                patent={selectedPatent}
                requestId={selectedRequestId}
                loadRequests={loadRequests}
            />

            {slaModalOpen && (
                <SLAUpdateModal
                    open={slaModalOpen}
                    onClose={handleCloseSlaModal}
                    request={selectedRequest}
                    loadRequests={loadRequests}
                />
            )}
        </>
    );
}

ReferenceTable.propTypes = {
    requests: PropTypes.array,
    loadRequests: PropTypes.func,
    loading: PropTypes.bool,
    setLoading: PropTypes.func,
};